import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from "@mui/icons-material/Menu";
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import logo from '../img/logo.png'; 
import Divider from '@mui/material/Divider';
import { auth, db } from '../firebaseConfig'; 
import { signOut, getAuth, onAuthStateChanged } from 'firebase/auth';
import SchoolIcon from '@mui/icons-material/School';
import CallIcon from '@mui/icons-material/Call';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import Avatar from "@mui/material/Avatar";
import ButtonBase from "@mui/material/ButtonBase";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
  doc,
  getDocs,
  deleteDoc, 
  addDoc,
  updateDoc, 
  serverTimestamp, 
  increment
} from "firebase/firestore";
import Badge from "@mui/material/Badge";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import ChatIcon from "@mui/icons-material/Chat";
import DeleteIcon from "@mui/icons-material/Delete";
import CircleIcon from "@mui/icons-material/Circle";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import "../i18n";
import ListItemIcon from "@mui/material/ListItemIcon";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InputBase from "@mui/material/InputBase";
import ArabicFlag from "../img/Arabic_flag.png";
import FrenchFlag from "../img/French_flag.png";
import EnglishFlag from "../img/English_flag.png";
import { getRoleLabels, getStoredTutorType } from "../utils/roleTerminology";

const LogoImage = styled('img')(({ theme }) => ({
  width: '150px',
  height: 'auto',
}));

const LANGS = [
  { code: "ar", label: "العربية", flagImg: ArabicFlag },
  { code: "fr", label: "French", flagImg: FrenchFlag },
  { code: "en", label: "English", flagImg: EnglishFlag },
];

const menuItemSx = {
  width: "100%",
  overflow: "hidden",
  borderRadius: 2,
  alignItems: "center",
  gap: 1.25,
  px: 1.5,
  py: 1.2,
};

const menuItemTextSx = {
  fontSize: 13.5,
  fontWeight: 800,
  color: "#111827",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  lineHeight: 1.3,
};

const menuItemSubTextSx = {
  fontSize: 12.5,
  color: "#6b7280",
  mt: 0.2,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  lineHeight: 1.3,
};

export default function MenuAppBar({ fixed = false }) {
  const { t, i18n } = useTranslation();
  const role = getRoleLabels(t, getStoredTutorType());
  const navigate = useNavigate();
  const [authState, setAuthState] = React.useState(true); 
  const [studentUnreadChats, setStudentUnreadChats] = useState(0);
  const [studentName, setStudentName] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const totalBellCount = notificationsCount + studentUnreadChats;
  const hasAnything = (notifications?.length > 0) || (studentUnreadChats > 0);
  const currentLang = LANGS.find((l) => l.code === (i18n.language || "ar")) || LANGS[0];

  const [langAnchorEl, setLangAnchorEl] = useState(null);

  const handleLangMenuOpen = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleSelectLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("darssy_lang", code);
    handleLangMenuClose();
  };

  useEffect(() => {
    let unsubscribeNotifs = null;

    const auth = getAuth();

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      // signed out
      if (!user) {
        setNotifications([]);
        setNotificationsCount(0);
        if (unsubscribeNotifs) {
          unsubscribeNotifs();
          unsubscribeNotifs = null;
        }
        return;
      }

      // If auth fires again, safely rebuild listener
      if (unsubscribeNotifs) {
        unsubscribeNotifs();
        unsubscribeNotifs = null;
      }

      // OPTION A (recommended): if your notifications are stored like:
      // students/<studentDocId>/notifications/<notifId>
      // and the student document id == auth.uid
      const notifsRef = collection(db, "students", user.uid, "notifications");

      unsubscribeNotifs = onSnapshot(
        query(notifsRef, orderBy("createdAt", "desc"), limit(50)),
        (snap) => {
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setNotifications(items);

          // count only unread if you have a field like "read: false"
          const unread = items.filter((n) => n.read !== true).length;
          setNotificationsCount(unread);
        },
        (err) => {
          console.error("Student notifications listener error:", err);
          setNotifications([]);
          setNotificationsCount(0);
        }
      );

      /**
       * OPTION B (use this instead if your student document ID is NOT the auth.uid)
       * and you store `uid` inside the student doc:
       *
       * const qStudent = query(collection(db,"students"), where("uid","==",user.uid));
       * onSnapshot(qStudent, (sSnap) => {
       *   if (sSnap.empty) return;
       *   const studentDocId = sSnap.docs[0].id;
       *   const notifsRef = collection(db,"students",studentDocId,"notifications");
       *   ...
       * });
       */
    });

    return () => {
      if (unsubscribeNotifs) unsubscribeNotifs();
      unsubscribeAuth();
    };
  }, []);


  // Realtime unread chat count for student (robust: supports studentId stored as auth.uid OR as studentDocId)
useEffect(() => {
  const auth = getAuth();

  let unsubStudentDoc = null;
  let unsubConvosByUid = null;
  let unsubConvosByDocId = null;

  // store both totals safely without accumulating
  const uidTotalRef = { current: 0 };
  const docIdTotalRef = { current: 0 };

  const setCombined = () => {
    setStudentUnreadChats(Number(uidTotalRef.current || 0) + Number(docIdTotalRef.current || 0));
  };

  const unsubAuth = onAuthStateChanged(auth, (user) => {
    if (!user) {
      uidTotalRef.current = 0;
      docIdTotalRef.current = 0;
      setStudentUnreadChats(0);

      if (unsubStudentDoc) unsubStudentDoc();
      if (unsubConvosByUid) unsubConvosByUid();
      if (unsubConvosByDocId) unsubConvosByDocId();

      unsubStudentDoc = null;
      unsubConvosByUid = null;
      unsubConvosByDocId = null;
      return;
    }

    // cleanup old listeners
    if (unsubStudentDoc) unsubStudentDoc();
    if (unsubConvosByUid) unsubConvosByUid();
    if (unsubConvosByDocId) unsubConvosByDocId();

    unsubStudentDoc = null;
    unsubConvosByUid = null;
    unsubConvosByDocId = null;

    // reset refs for fresh session
    uidTotalRef.current = 0;
    docIdTotalRef.current = 0;
    setCombined();

    // A) conversations where studentId == auth.uid
    const qByUid = query(collection(db, "conversations"), where("studentId", "==", user.uid));
    unsubConvosByUid = onSnapshot(
      qByUid,
      (snap) => {
        let total = 0;
        snap.forEach((d) => {
          const data = d.data() || {};
          total += Number(data.unreadCountStudent || 0);
        });
        uidTotalRef.current = total;
        setCombined();
      },
      (err) => {
        console.error("Student unread chat listener (by uid) error:", err);
        uidTotalRef.current = 0;
        setCombined();
      }
    );

    // B) conversations where studentId == studentDocId (students collection auto-id)
    const qStudent = query(collection(db, "students"), where("uid", "==", user.uid));
    unsubStudentDoc = onSnapshot(
      qStudent,
      (sSnap) => {
        if (sSnap.empty) return;

        const studentDocId = sSnap.docs[0].id;
        const studentData = sSnap.docs[0].data() || {};
        setStudentName(studentData.firstName || studentData.fullName || "");

        // If docId == uid, no need for second listener
        if (studentDocId === user.uid) {
          docIdTotalRef.current = 0;
          setCombined();
          return;
        }

        const qByDocId = query(collection(db, "conversations"), where("studentId", "==", studentDocId));
        unsubConvosByDocId = onSnapshot(
          qByDocId,
          (cSnap) => {
            let total = 0;
            cSnap.forEach((d) => {
              const data = d.data() || {};
              total += Number(data.unreadCountStudent || 0);
            });
            docIdTotalRef.current = total;
            setCombined();
          },
          (err) => {
            console.error("Student unread chat listener (by docId) error:", err);
            docIdTotalRef.current = 0;
            setCombined();
          }
        );
      },
      (err) => {
        console.error("Student doc lookup listener error:", err);
      }
    );
  });

  return () => {
    if (unsubStudentDoc) unsubStudentDoc();
    if (unsubConvosByUid) unsubConvosByUid();
    if (unsubConvosByDocId) unsubConvosByDocId();
    unsubAuth();
  };
}, []);


  const handleNotificationsMenu = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    if (notification?.id) {
      await handleDeleteNotification(notification.id);
    }

    // adjust to your stored shape
    if (notification.type === "chat") {
      navigate("/student/chat");
      handleNotificationsClose();
      return;
    }

    if (notification.type === "forum") {
      const { groupId, postId } = notification;
      navigate(`/student/class/${groupId}/forum?postId=${postId}`);
      handleNotificationsClose();
      return;
    }

    // fallback
    handleNotificationsClose();
  };

  const markAllStudentChatsAsRead = async () => {
    try {
      const user = getAuth().currentUser;
      if (!user) return;

      const processedConversationIds = new Set();

      const resetUnreadForStudentId = async (studentIdValue) => {
        if (!studentIdValue) return;

        const conversationsQuery = query(
          collection(db, "conversations"),
          where("studentId", "==", studentIdValue)
        );
        const snapshot = await getDocs(conversationsQuery);
        const updates = [];

        snapshot.forEach((conversationDoc) => {
          if (processedConversationIds.has(conversationDoc.id)) return;
          processedConversationIds.add(conversationDoc.id);

          const data = conversationDoc.data() || {};
          const unread = Number(data.unreadCountStudent || 0);
          if (unread > 0) {
            updates.push(
              updateDoc(doc(db, "conversations", conversationDoc.id), {
                unreadCountStudent: 0,
              })
            );
          }
        });

        await Promise.all(updates);
      };

      await resetUnreadForStudentId(user.uid);

      const studentDocQuery = query(collection(db, "students"), where("uid", "==", user.uid));
      const studentDocSnapshot = await getDocs(studentDocQuery);
      if (!studentDocSnapshot.empty) {
        await resetUnreadForStudentId(studentDocSnapshot.docs[0].id);
      }
    } catch (error) {
      console.error("Error marking student chats as read:", error);
    }
  };

  const handleMessagesNotificationClick = async () => {
    await markAllStudentChatsAsRead();
    navigate("/student/chat");
    handleNotificationsClose();
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      const user = getAuth().currentUser;
      if (!user) return;

      // assuming notifications live here: students/<uid>/notifications/<notifId>
      await deleteDoc(doc(db, "students", user.uid, "notifications", notificationId));
    } catch (e) {
      console.error("Error deleting student notification:", e);
    }
  };


  const handleNavigate = (path) => {
    navigate(path);
    handleClose();
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth); 
      navigate('/student/login'); 
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      handleClose(); 
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <AppBar
        position={fixed ? "sticky" : "static"}
        elevation={0}
        sx={{
          backgroundColor: "#ffffff",
          color: "#111827",
          borderBottom: "1px solid #e5e7eb",
          ...(fixed ? { top: 0, zIndex: (theme) => theme.zIndex.appBar } : {}),
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", gap: 2 }}>
          {/* On the docked (sidebar) layout the logo lives in the sidebar's own
              header instead — only shown here when there's no sidebar to carry
              it: standalone/legacy pages (fixed=false), or mobile where the
              sidebar is hidden. */}
          <Box
            sx={{
              display: fixed ? { xs: "flex", md: "none" } : "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <LogoImage src={logo} alt={t("Logo")} />
          </Box>

          {/* Search (decorative — not wired to any search functionality yet) */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              flex: 1,
              maxWidth: 480,
              height: 40,
              boxSizing: "border-box",
              bgcolor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              px: 1.75,
              gap: 1,
            }}
          >
            <SearchOutlinedIcon sx={{ fontSize: 20, color: "#9ca3af" }} />
            <InputBase
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("Search…")}
              sx={{
                flex: 1,
                fontSize: 14,
                color: "#111827",
                "& input::placeholder": { color: "#9ca3af", opacity: 1 },
              }}
            />
          </Box>

          {authState && (
            <div>
              {/* Language switch (dropdown) */}
              <Tooltip title={currentLang.label} arrow>
                <IconButton
                  aria-label={t('Language')}
                  aria-controls="language-menu"
                  aria-haspopup="true"
                  onClick={handleLangMenuOpen}
                  sx={{
                    height: 40,
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "#f3f4f6" },
                    mr: 1,
                    border: "1px solid #eef2f7",
                    bgcolor: "#ffffff",
                    px: 1.5,
                    gap: 0.5,
                    display: "inline-flex",
                    alignItems: "center",
                    verticalAlign: "middle",
                  }}
                >
                  <LanguageOutlinedIcon sx={{ fontSize: 18, color: "#4b5563" }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827", display: { xs: "none", md: "inline" } }}>
                    {currentLang.label}
                  </Typography>
                  <KeyboardArrowDownIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                </IconButton>
              </Tooltip>
              {/* Notification Bell Icon */}
              <IconButton
                aria-label={t('Notifications')}
                aria-controls="notification-menu"
                aria-haspopup="true"
                onClick={handleNotificationsMenu}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "14px",
                  "&:hover": { bgcolor: "#f3f4f6" },
                  mr: 1,
                }}
              >
                <Badge
                  badgeContent={notificationsCount + studentUnreadChats}
                  color="error"
                  overlap="circular"
                  anchorOrigin={{ vertical: "top", horizontal: "left" }}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontWeight: 800,
                      minWidth: 18,
                      height: 18,
                      fontSize: 11,
                      borderRadius: "999px",
                      border: "2px solid #ffffff",
                    },
                  }}
                >
                  <NotificationsNoneOutlinedIcon sx={{ fontSize: 22, color: "#111827" }} />
                </Badge>
              </IconButton>
              {studentName && (
                <ButtonBase
                  onClick={handleMenu}
                  aria-label={t('Account menu')}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    borderRadius: "999px",
                    px: 1,
                    py: 0.5,
                    mr: 1,
                    "&:hover": { bgcolor: "#f3f4f6" },
                  }}
                >
                  <Avatar sx={{ width: 36, height: 36, bgcolor: "#e8f0fe", color: "#2f6fed", fontWeight: 700 }}>
                    {studentName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ textAlign: "right", display: { xs: "none", sm: "block" } }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
                      {studentName}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#6b7280", lineHeight: 1.2 }}>
                      {role.learnerSingular}
                    </Typography>
                  </Box>
                  <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#9ca3af", display: { xs: "none", sm: "inline-flex" } }} />
                </ButtonBase>
              )}
              {/* Menu trigger — desktop uses the avatar's chevron above; this stays
                  as the sole entry point only below the sidebar's md breakpoint. */}
              <IconButton
                aria-label={t('Open menu')}
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "14px",
                  "&:hover": { bgcolor: "#f3f4f6" },
                  display: { xs: "inline-flex", md: "none" },
                }}
              >
                <MenuIcon sx={{ fontSize: 22, color: "#111827" }} />
              </IconButton>

              <Menu
                id="language-menu"
                anchorEl={langAnchorEl}
                open={Boolean(langAnchorEl)}
                onClose={handleLangMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      width: 280,
                      borderRadius: 3,
                      overflow: "hidden",
                      mt: 1,
                      boxShadow: "0 18px 45px rgba(0,0,0,0.12)",
                      border: "1px solid rgba(0,0,0,0.06)",
                      direction: "rtl",
                    },
                  },
                }}
                MenuListProps={{ sx: { p: 0 } }}
              >
                {/* Header */}
                <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff", borderBottom: "1px solid #eef2f7" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
                    {t("Language")}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
                    {t("Choose app language")}
                  </Typography>
                </Box>

                {/* Body */}
                <Box sx={{ p: 1 }}>
                  {LANGS.map((lng) => {
                    const selected = (i18n.language || "ar") === lng.code;

                    return (
                      <MenuItem
                        key={lng.code}
                        onClick={() => handleSelectLanguage(lng.code)}
                        sx={{
                          borderRadius: 2,
                          alignItems: "center",
                          gap: 1.25,
                          px: 1.5,
                          py: 1.15,
                          "&:hover": { bgcolor: "#f9fafb" },
                          bgcolor: selected ? "rgba(79,70,229,0.08)" : "transparent",
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 34 }}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "999px",
                              display: "grid",
                              placeItems: "center",
                              bgcolor: selected ? "rgba(79,70,229,0.12)" : "#f3f4f6",
                              color: selected ? "#4f46e5" : "#4b5563",
                              fontSize: 11,
                              fontWeight: 800,
                            }}
                          >
                            {lng.code.toUpperCase()}
                          </Box>
                        </ListItemIcon>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#111827" }}>
                            {lng.label}
                          </Typography>
                        </Box>

                        {selected && <CircleIcon sx={{ fontSize: 10, color: "#4f46e5" }} />}
                      </MenuItem>
                    );
                  })}
                </Box>
              </Menu>

              {/* Notifications Dropdown Menu */}
              <Menu
                id="notification-menu"
                anchorEl={notificationsAnchorEl}
                open={Boolean(notificationsAnchorEl)}
                onClose={handleNotificationsClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      width: 360,
                      borderRadius: 3,
                      overflow: "hidden",
                      mt: 1,
                      boxShadow: "0 18px 45px rgba(0,0,0,0.12)",
                      border: "1px solid rgba(0,0,0,0.06)",
                      direction: "rtl",
                    },
                  },
                }}
                MenuListProps={{ sx: { p: 0 } }}
              >
                {/* Header */}
                <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff", borderBottom: "1px solid #eef2f7" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
                    {t("Notifications")}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
                    {totalBellCount > 0 ? t("You have {{count}} new notification(s)", { count: totalBellCount }) : t("No new notifications")}
                  </Typography>
                </Box>

                {/* Body */}
                {hasAnything ? (
                  <Box sx={{ p: 1, maxHeight: 320, overflowY: "auto" }}>

                    {/* ✅ Messages notification (show only if unread chats exist) */}
                    {studentUnreadChats > 0 && (
                      <MenuItem
                        onClick={handleMessagesNotificationClick}
                        sx={{
                          borderRadius: 2,
                          alignItems: "flex-start",
                          gap: 1.25,
                          px: 1.5,
                          py: 1.25,
                          "&:hover": { bgcolor: "#f9fafb" },
                          mb: 0.5,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "rgba(16,185,129,0.12)",
                            color: "#10b981",
                            flexShrink: 0,
                          }}
                        >
                          <ChatIcon sx={{ fontSize: 20 }} />
                        </Avatar>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#111827" }}>
                              {t("New messages")}
                            </Typography>
                            <CircleIcon sx={{ fontSize: 8, color: "#ef4444" }} />
                          </Box>

                          <Typography sx={{ fontSize: 13, color: "#4b5563", mt: 0.25, lineHeight: 1.4 }}>
                            {t("You have {{count}} unread message(s). Tap to open conversations.", { count: studentUnreadChats })}
                          </Typography>

                          <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
                            {t("Tap to open conversations")}
                          </Typography>
                        </Box>
                      </MenuItem>
                    )}

                    {/* ✅ Forum/other notifications */}
                    {notifications.map((notification) => {
                      const isChat = notification.type === "chat";

                      return (
                        <MenuItem
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          sx={{
                            borderRadius: 2,
                            alignItems: "flex-start",
                            gap: 1.25,
                            px: 1.5,
                            py: 1.25,
                            "&:hover": { bgcolor: "#f9fafb" },
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: isChat ? "rgba(16,185,129,0.12)" : "rgba(79,70,229,0.12)",
                              color: isChat ? "#10b981" : "#4f46e5",
                              flexShrink: 0,
                            }}
                          >
                            {isChat ? (
                              <ChatIcon sx={{ fontSize: 20 }} />
                            ) : (
                              <QuestionAnswerIcon sx={{ fontSize: 20 }} />
                            )}
                          </Avatar>

                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#111827" }}>
                                {isChat ? t("New message") : t("New forum question")}
                              </Typography>
                              {notification.read !== true && <CircleIcon sx={{ fontSize: 8, color: "#ef4444" }} />}
                            </Box>

                            <Typography
                              sx={{
                                fontSize: 13,
                                color: "#4b5563",
                                mt: 0.25,
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                lineHeight: 1.4,
                              }}
                            >
                              {isChat
                                ? t("{{name}} sent you a message.", { name: notification.senderName || t("Someone") })
                                : t("{{name}} asked a question in the forum.", { name: notification.full_name || t("A student") })}
                            </Typography>

                            <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
                              {t("Tap to view details")}
                            </Typography>
                          </Box>

                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(notification.id);
                            }}
                            sx={{ color: "#ef4444", mt: 0.2 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </MenuItem>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ p: 2.5, textAlign: "center" }}>
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        mx: "auto",
                        bgcolor: "#f3f4f6",
                        color: "#6b7280",
                      }}
                    >
                      <NotificationsNoneOutlinedIcon />
                    </Avatar>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, mt: 1, color: "#111827" }}>
                      {t("No notifications")}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "#6b7280", mt: 0.5 }}>
                      {t("New notifications will appear here when they arrive.")}
                    </Typography>
                  </Box>
                )}
              </Menu>

              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      width: 320,
                      borderRadius: 3,
                      overflow: "hidden",
                      mt: 1,
                      boxShadow: "0 18px 45px rgba(0,0,0,0.12)",
                      border: "1px solid rgba(0,0,0,0.06)",
                      direction: "rtl",
                    },
                  },
                }}
                MenuListProps={{ sx: { p: 0 } }}
              >
              {/* Header */}
              <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff", borderBottom: "1px solid #eef2f7" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
                  {t("Menu")}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
                  {t("Choose the page you want to open")}
                </Typography>
              </Box>

              {/* Body */}
              <Box sx={{ p: 1 }}>
                <MenuItem
                  onClick={() => handleNavigate("/student/classes")}
                  sx={{
                    ...menuItemSx,
                    "&:hover": { bgcolor: "#f9fafb" },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: "rgba(18,52,77,0.10)",
                      color: "#12344d",
                      flexShrink: 0,
                    }}
                  >
                    <SchoolIcon sx={{ fontSize: 20 }} />
                  </Avatar>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={menuItemTextSx}>
                      {t("Home")}
                    </Typography>
                    <Typography sx={menuItemSubTextSx}>
                      {role.isCoach ? t("View your programs") : t("View your classes")}
                    </Typography>
                  </Box>
                </MenuItem>

                <MenuItem
                  onClick={() => handleNavigate("/student/contactUs")}
                  sx={{
                    ...menuItemSx,
                    "&:hover": { bgcolor: "#f9fafb" },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: "rgba(16,185,129,0.12)",
                      color: "#10b981",
                      flexShrink: 0,
                    }}
                  >
                    <CallIcon sx={{ fontSize: 20 }} />
                  </Avatar>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={menuItemTextSx}>
                      {t("Contact administration")}
                    </Typography>
                    <Typography sx={menuItemSubTextSx}>
                      {t("Send a message or complaint to administration")}
                    </Typography>
                  </Box>
                </MenuItem>

                <Divider sx={{ my: 1 }} />

                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    ...menuItemSx,
                    "&:hover": { bgcolor: "#fef2f2" },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: "rgba(239,68,68,0.12)",
                      color: "#ef4444",
                      flexShrink: 0,
                    }}
                  >
                    <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 20 }} />
                  </Avatar>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={menuItemTextSx}>
                      {t("Log out")}
                    </Typography>
                    <Typography sx={menuItemSubTextSx}>
                      {t("Sign out of the student account")}
                    </Typography>
                  </Box>
                </MenuItem>
              </Box>
              </Menu>
            </div>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  );
}
